import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { branchImages } from "../../constants/restaurants";
import { useDispatch, useSelector } from "react-redux";
import { emptyCartAsync, getBranchDetailsAsync } from "../../redux/features/restaurants/restaurantThunks";
import {
  selectSelectedAddress,
  selectActiveOrders,
  selectServiceType,
  selectCreatedCustomer,
} from "../../redux/features/customer/customerSelectors";
import {
  selectCartItems,
  selectCartRestaurantDetails,
} from "../../redux/features/restaurants/restaurantSelectors";
import { setCartRestaurantDetails } from "../../redux/features/restaurants/restaurantSlice";
import WarningModal from "../modal";
import { clearCart } from "../../redux/features/restaurants/restaurantSlice";
import { setPastOrderDetails } from "../../redux/features/order/orderSlice";
import { selectPastOrderDetails } from "../../redux/features/order/orderSelectors";

export const AvailableBrands = ({ branchesData, onSelectRestaurant, selectedRestaurantId }) => {
  const selectedAddress = useSelector(selectSelectedAddress);
  const existingRestaurantDetails = useSelector(selectCartRestaurantDetails);
  const cartItems = useSelector(selectCartItems);
  const activeOrders = useSelector(selectActiveOrders);
  const serviceType = useSelector(selectServiceType);
  const pastOrderDetails = useSelector(selectPastOrderDetails);
  const customerData = useSelector(selectCreatedCustomer);

  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [showWarningModal, setWarningModal] = useState(false);
  const [restaurantToSwitchTo, setRestaurantToSwitchTo] = useState(null);


  // Get current service type (pickup or delivery)
  const currentServiceType = serviceType || "pickup";

  // Check if a restaurant has an active order with matching service type
  // For delivery orders, also check if address matches
  const hasActiveOrder = (restaurantId) => {
    return activeOrders.some((order) => {
      // Restaurant ID must match
      const restaurantMatch = order.restaurant_id === restaurantId;

      // Service type must match
      const serviceTypeMatch = order.service_type === currentServiceType;

      // For delivery orders, also check address match
      if (currentServiceType === "delivery") {
        // Only show active if both restaurant, service type, and address match
        return (
          restaurantMatch &&
          serviceTypeMatch &&
          selectedAddress &&
          order.address_id === selectedAddress.id
        );
      } else {
        // For pickup, only check restaurant and service type
        return restaurantMatch && serviceTypeMatch;
      }
    });
  };

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Proceed with switching restaurants after user confirms
  const handleProceed = () => {
    dispatch(clearCart());
    dispatch(emptyCartAsync(customerData?.id));

    if(pastOrderDetails){
      dispatch(setPastOrderDetails(null));
    }
    if (restaurantToSwitchTo) {
      // If we're in the checkout flow, just notify the parent
      if (onSelectRestaurant) {
        onSelectRestaurant(restaurantToSwitchTo.id);
        setSelectedRestaurant(restaurantToSwitchTo);
      } else {
        // Otherwise, navigate to place-order
        handleBrandSelect(restaurantToSwitchTo);
      }
      // Reset after handling
      setRestaurantToSwitchTo(null);
    }
    setWarningModal(false);
  };

  const handleCancel = () => {
    setWarningModal(false);
    setRestaurantToSwitchTo(null);
  };

  const handleBrandSelect = async (branch) => {
    console.log("Current service type:", currentServiceType);
    console.log("Branch selected:", branch);
    
    // For catering, store restaurant details and navigate directly to catering packages
    if (currentServiceType === "individual catering" || currentServiceType === "corporate catering") {
      console.log("Storing restaurant details for catering:", branch);
      // Store the selected restaurant details in Redux for catering
      dispatch(setCartRestaurantDetails({
        restaurant_id: branch.id,
        restaurantName: branch.name_en,
        branchId: null, // Not needed for catering
        branchName: null,
      }));
      
      console.log("Navigating to catering packages...");
      navigate("/catering-packages");
      return;
    }
    
    // For non-catering, fetch branch details first
    const result = await dispatch(
      getBranchDetailsAsync(branch.id, selectedAddress?.area, branch.name_en)
    );
    
    console.log("Branch details result:", result);
    
    // Check if we successfully got the data
    if (result && result.success && result.data) {
      // Log what's in the result.data to diagnose
      console.log("Branch hours data:", result.data.branch_hours);
      
      // Always proceed to menu page regardless of branch hours
      navigate("/place-order");
    } else {
      console.log("Failed to get branch details or no delivery available");
      // Failed to get branch details or no delivery to the area
    }
  };

  // Check if we need to show the warning modal
  const shouldShowWarningModal = (restaurant) => {
    // Only show warning if:
    // 1. There are items in the cart
    // 2. The cart has a different restaurant than the one being selected
    return (
      cartItems.length > 0 && 
      existingRestaurantDetails?.restaurantId !== restaurant.id
    );
  };

  const handleRestaurantClick = (restaurant) => {
    // Always update the visual selection
    setSelectedRestaurant(restaurant);
    
    // Check if we need to show the warning modal
    if (shouldShowWarningModal(restaurant)) {
      // Store the restaurant to switch to after confirmation
      setRestaurantToSwitchTo(restaurant);
      setWarningModal(true);
    } else {
      // No warning needed, proceed normally
      if (onSelectRestaurant) {
        onSelectRestaurant(restaurant.id);
      } else {
        handleBrandSelect(restaurant);
      }
    }
  };

  // Set the selected restaurant based on the provided prop or cart items
  useEffect(() => {
    // Priority 1: If selectedRestaurantId prop is provided, use it
    if (selectedRestaurantId) {
      setSelectedRestaurant(branchesData?.find(branch => branch.id === selectedRestaurantId) || null);
    } 
    // Priority 2: If cart has items, automatically select that restaurant
    else if (cartItems.length > 0 && existingRestaurantDetails?.restaurantId) {
      const cartRestaurant = branchesData?.find(
        branch => branch.id === existingRestaurantDetails.restaurantId
      );
      if (cartRestaurant) {
        setSelectedRestaurant(cartRestaurant);
        // If onSelectRestaurant callback exists, notify parent component
        if (onSelectRestaurant) {
          onSelectRestaurant(cartRestaurant.id);
        }
      }
    }
  }, [selectedRestaurantId, cartItems, existingRestaurantDetails, branchesData, onSelectRestaurant]);

  return (
    <>
      <h1 className="text-3xl font-bold text-gray-500">
        Available Brands to Order
      </h1>
      <div className="my-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 w-full max-w-screen-xl mx-auto">
          {branchesData?.map((branch) => (
            <div
              key={branch.id}
              onClick={() => handleRestaurantClick(branch)}
              className={`relative flex flex-col items-center justify-center shadow-lg rounded-xl h-[162px] p-4 cursor-pointer transition-all duration-300 ${
                selectedRestaurant?.id === branch.id 
                  ? "bg-gray-50 border-4 border-[#86604D]" 
                  : "bg-[#C0CDD7] hover:border-4 hover:border-[#86604D]"
              }`}
            >
              {/* Active Order Badge */}
              {hasActiveOrder(branch.id) && (
                <button className="absolute top-3 right-2 w-20 h-5 bg-emerald-600 text-white border border-white rounded-lg font-bold text-xs flex-shrink-0">
                  Active Order
                </button>
              )}
              
              {/* Restaurant Logo */}
              <img
                src={branchImages[branch.name_en] || "/Images/default.png"}
                alt={branch.name_en}
                className="max-h-[100px] object-contain"
              />
            </div>
          ))}
        </div>
        {showWarningModal && restaurantToSwitchTo && (
          <WarningModal
            onCancel={handleCancel}
            onProceed={handleProceed}
            description={`You're about to change restaurant from ${existingRestaurantDetails?.restaurantName} to ${restaurantToSwitchTo?.name_en}`}
            subDescription={
              "Current cart will be emptied, Do you wish to proceed?"
            }
            confirmButtonText="Confirm"
            cancelButtonText="Cancel"
            showItems={false}
          />
        )}
      </div>
    </>
  );
};
